import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Shield, Building2, Package, UtensilsCrossed,
  FolderOpen, Folder, IndianRupee, Ticket, TrendingUp,
  Activity, ShoppingCart, Package2, AlertCircle, UserPlus, Clock,
  RefreshCw, Download, Filter, Calendar,
  XCircle,
  CheckCircle2,
  Coffee,
  Truck,
  Ban,
  ArrowUpRight,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { cn } from 'lib/utils';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

import { Play, Square, Timer } from 'lucide-react';
import { fetchBreakLogs, createBreakLog, punchOutBreakLog, exportBreakLogs } from 'store/slices/breakTimeLogSlice';


import { fetchOrders } from 'store/slices/orderSlice';
import { fetchEmployees } from 'store/slices/employeeSlice';
import { fetchMenus } from 'store/slices/canteenSlice';
import { fetchRoles } from 'store/slices/roleSlice';
import { fetchDepartments } from 'store/slices/departmentSlice';
import { fetchCategories } from 'store/slices/categorySlice';
import { fetchSubcategories } from 'store/slices/subcategorySlice';
import { fetchInventories } from 'store/slices/inventorySlice';
import { fetchTickets } from 'store/slices/ticketSlice';
import useCanteenSocket from 'hooks/useCanteenSocket';
import useNotificationSocket from 'hooks/useNotificationSocket';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';
import DataTable from 'components/common/DataTable';
import { toast } from 'sonner';
import Attendance from 'components/common/Attendance';
import { isFullSuperAdmin } from 'utlis/authUtils';

const Dashboard = () => {
  usePermissionSocket();
  useCanteenSocket();
  useNotificationSocket();
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.auth.user);
  const currentUserId = currentUser?.id || localStorage.getItem('userId'); // fallback if needed

  const showFullDashboard = isFullSuperAdmin();

  // NEW: Add loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('current_month'); // NEW: Date range filter
  const [orderStatsView, setOrderStatsView] = useState('today'); // NEW: Order stats view filter

  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendancePageSize, setAttendancePageSize] = useState(10);
  const [attendanceSearch, setAttendanceSearch] = useState('');
  const [modalLogs, setModalLogs] = useState([]); // Local logs for modal
  const [modalPagination, setModalPagination] = useState({ count: 0, page_size: 10 });
  const [modalLoading, setModalLoading] = useState(false);

  const { canExport: canExportBreakLogs = false } = usePermissions('break_time_log');

  // PERMISSIONS
  const { canRead: canViewEmployee } = usePermissions('employee');
  const canViewRole = usePermissions('role').canRead;
  const canViewDepartment = usePermissions('department').canRead;
  const canViewCategory = usePermissions('category').canRead;
  const canViewSubcategory = usePermissions('sub_category').canRead;
  const canViewInventory = usePermissions('inventory').canRead;
  const canViewMenu = usePermissions('menu').canRead;
  const canViewOrder = usePermissions('canteen_order_log').canRead;
  const canViewTicket = usePermissions('ticket').canRead;

  // NEW: Refresh function
  const refreshData = () => {
    setIsLoading(true);
    setError(null);
    fetchData();
  };

  const { canRead } = usePermissions('dashboard');

  const refetchAttendance = useCallback(() => {
    dispatch(
      fetchBreakLogs({
        page: attendancePage,
        pageSize: attendancePageSize,
        search: attendanceSearch,
        title: 'attendance',
      })
    );
  }, [dispatch, attendancePage, attendancePageSize, attendanceSearch]);

  useEffect(() => {
    if (isAttendanceModalOpen) {
      refetchAttendance();
    }
  }, [isAttendanceModalOpen, refetchAttendance]);

  // === Attendance State ===
  const { logs: allBreakLogs = [], isLoading: breakLoading } = useSelector(
    (state) => state.breakTimeLog || { logs: [], isLoading: false }
  );

  // === FILTER: Only current user's break logs ===
  const userBreakLogs = useMemo(() => {
    if (!currentUserId) return [];
    return allBreakLogs.filter(log => log.employee === currentUserId);
  }, [allBreakLogs, currentUserId]);

  // === Active break for current user only ===
  const activeBreak = useMemo(() => {
    return userBreakLogs.find(log => !log.punch_out);
  }, [userBreakLogs]);

  //     const todayLogs = useMemo(() => {
  //   const today = new Date().toDateString();
  //   return userBreakLogs
  //     .filter((log) => new Date(log.punch_in).toDateString() === today)
  //     .slice(0, 5);
  // }, [userBreakLogs]);


  // === Fetch break logs (latest 10 for current user) ===
  useEffect(() => {
    if (currentUserId) {
      dispatch(fetchBreakLogs({ page: 1, pageSize: 5 }));
    }
  }, [dispatch, currentUserId]);


  useEffect(() => {
    dispatch(fetchBreakLogs());
  }, [dispatch]);


  const handleCheckIn = () => {
    dispatch(
      createBreakLog({
        punch_in: new Date().toISOString(),
        title: 'attendance',
      })
    );
  };

  const handleCheckOut = () => {
    if (!activeBreak) return;

    dispatch(
      punchOutBreakLog({
        id: activeBreak.id,
        punch_out: new Date().toISOString(),
        title: 'attendance',
      })
    );
  };



  // all hsotru attabnedc
  const todayLogs = useMemo(() => {
    const today = new Date().toDateString();
    return userBreakLogs
      .filter((log) => new Date(log.punch_in).toDateString() === today)
      .slice(0, 5);
  }, [userBreakLogs]);

  // Refetch for modal with employee filter
  const refetchAttendanceModal = useCallback(() => {
    if (!currentUserId) return;

    setModalLoading(true);
    dispatch(
      fetchBreakLogs({
        page: attendancePage,
        pageSize: attendancePageSize,
        search: attendanceSearch,
        title: 'attendance',
        employee: currentUserId, // Critical: backend filter
      })
    )
      .unwrap()
      .then((response) => {
        setModalLogs(response.data || []);
        setModalPagination(response.pagination || { count: 0, page_size: 10 });
      })
      .catch(() => {
        toast.error('Failed to load attendance history');
      })
      .finally(() => {
        setModalLoading(false);
      });
  }, [dispatch, currentUserId, attendancePage, attendancePageSize, attendanceSearch]);

  // Trigger refetch when modal opens or filters change
  useEffect(() => {
    if (isAttendanceModalOpen) {
      refetchAttendanceModal();
    }
  }, [isAttendanceModalOpen, refetchAttendanceModal]);

  // Export handler
  const handleExportAttendance = () => {
    if (!canExportBreakLogs) {
      toast.error("You don't have permission to export");
      return;
    }
    dispatch(exportBreakLogs());
    toast.success('Attendance export started...');
  };


  // NEW: Organize data fetching in a single function
  const fetchData = () => {
    const promises = [];

    if (canViewEmployee) promises.push(dispatch(fetchEmployees({ page: 1, pageSize: 100 })));
    if (canViewRole) promises.push(dispatch(fetchRoles({ page: 1, pageSize: 100 })));
    if (canViewDepartment) promises.push(dispatch(fetchDepartments({ page: 1, pageSize: 100 })));
    if (canViewCategory) promises.push(dispatch(fetchCategories({ page: 1, pageSize: 100 })));
    if (canViewSubcategory) promises.push(dispatch(fetchSubcategories({ page: 1, pageSize: 100 })));
    if (canViewInventory) promises.push(dispatch(fetchInventories({ page: 1, pageSize: 1000 })));
    if (canViewMenu) promises.push(dispatch(fetchMenus()));
    if (canViewOrder) promises.push(dispatch(fetchOrders({ page: 1, pageSize: 1000 })));
    if (canViewTicket) promises.push(dispatch(fetchTickets({ page: 1, pageSize: 100 })));

    Promise.all(promises)
      .then(() => setIsLoading(false))
      .catch(err => {
        setError(err.message || 'Failed to load dashboard data');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [
    canViewEmployee, canViewRole, canViewDepartment,
    canViewCategory, canViewSubcategory, canViewInventory,
    canViewMenu, canViewOrder, canViewTicket
  ]);

  const {
    employees = [], roles = [], departments = [], categories = [], subcategories = [],
    items = [], menus = [], orders = [],
    stats: orderStats = {
      pending: 0,
      preparing: 0,
      ready: 0,
      today_total: 0,
      today_revenue: 0,
      delivered: 0,
      cancelled: 0,
      today_total_pending: 0,
      today_total_preparing: 0,
      today_total_ready: 0,
      today_total_delivered: 0,
      today_total_cancelled: 0,
      monthly_total: 0,
      monthly_total_delivered: 0,
      monthly_total_cancelled: 0,
      monthly_revenue: 0
    },
    tickets = []
  } = useSelector((state) => ({
    employees: canViewEmployee ? (state.employee.employees || []) : [],
    roles: canViewRole ? (state.role.roles || []) : [],
    departments: canViewDepartment ? (state.department.departments || []) : [],
    categories: canViewCategory ? (state.category.categories || []) : [],
    subcategories: canViewSubcategory ? (state.subcategory.subcategories || []) : [],
    items: canViewInventory ? (state.inventory.items || []) : [],
    menus: canViewMenu ? (state.canteen.menus || []) : [],
    orders: canViewOrder ? (state.order.orders || []) : [],
    stats: canViewOrder ? (state.order.stats || {
      pending: 0,
      preparing: 0,
      ready: 0,
      today_total: 0,
      today_revenue: 0,
      delivered: 0,
      cancelled: 0,
      today_total_pending: 0,
      today_total_preparing: 0,
      today_total_ready: 0,
      today_total_delivered: 0,
      today_total_cancelled: 0,
      monthly_total: 0,
      monthly_total_delivered: 0,
      monthly_total_cancelled: 0,
      monthly_revenue: 0
    }) : {
      pending: 0,
      preparing: 0,
      ready: 0,
      today_total: 0,
      today_revenue: 0,
      delivered: 0,
      cancelled: 0,
      today_total_pending: 0,
      today_total_preparing: 0,
      today_total_ready: 0,
      today_total_delivered: 0,
      today_total_cancelled: 0,
      monthly_total: 0,
      monthly_total_delivered: 0,
      monthly_total_cancelled: 0,
      monthly_revenue: 0
    },
    tickets: canViewTicket ? (state.ticket.tickets || []) : [],
  }));

  // Helper functions to get date ranges
  const getStartAndEndOfDay = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return { start, end };
  };

  const getStartAndEndOfWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 (Sun) to 6 (Sat)
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Monday start
    const start = new Date(now.setDate(diff));
    start.setHours(0, 0, 0, 0);
    const end = new Date(now.setDate(diff + 6));
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const getStartAndEndOfMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return { start, end };
  };

  const monthlyRevenueData = useMemo(() => {
    const data = [];
    let chartTitle = '';

    // Helper: Format full date like "Tue, 10 Dec 2025"
    const formatFullDate = (date) =>
      date.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });

    // Helper: Format week range like "8–14 Dec 2025"
    const formatWeekRange = () => {
      const { start } = getStartAndEndOfWeek();
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const startDay = start.getDate();
      const endDay = end.getDate();
      const monthYear = start.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

      return `${startDay}–${endDay} ${monthYear}`;
    };

    switch (dateRange) {
      case 'current_day': {
        const today = new Date();
        const formattedDate = formatFullDate(today);

        const { start, end } = getStartAndEndOfDay();
        const dayOrders = orders.filter(order => {
          const created = new Date(order.created_at);
          return created >= start && created <= end;
        });
        const revenue = dayOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);

        data.push({
          day: `Today (${formattedDate})`,
          revenue
        });
        chartTitle = `Today's Revenue: ₹${revenue.toLocaleString('en-IN')} (${formattedDate})`;
        break;
      }

      case 'current_week': {
        const weekRange = formatWeekRange();
        const { start } = getStartAndEndOfWeek();
        const weekOrders = orders.filter(order => {
          const created = new Date(order.created_at);
          return created >= start && created <= new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
        });

        const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(start);
          dayDate.setDate(start.getDate() + i);
          const dayName = daysOfWeek[dayDate.getDay()];
          const dayLabel = `${dayName}, ${dayDate.getDate()} ${dayDate.toLocaleDateString('en-IN', { month: 'short' })}`;

          const dayStart = new Date(dayDate);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(dayDate);
          dayEnd.setHours(23, 59, 59, 999);

          const dayRevenue = weekOrders
            .filter(o => {
              const created = new Date(o.created_at);
              return created >= dayStart && created <= dayEnd;
            })
            .reduce((sum, o) => sum + (o.total_amount || 0), 0);

          data.push({ day: dayLabel, revenue: dayRevenue });
        }
        chartTitle = `This Week's Daily Revenue – ${weekRange}`;
        break;
      }

      case 'current_month': {
        const currentMonthName = new Date().toLocaleDateString('en-IN', {
          month: 'short',
          year: 'numeric'
        });

        data.push({
          month: currentMonthName,
          revenue: orderStats.monthly_revenue || 0
        });
        chartTitle = `Current Month Revenue: ₹${(orderStats.monthly_revenue || 0).toLocaleString('en-IN')}`;
        break;
      }

      default: { // 3months, 6months, 12months
        const monthsToShow = dateRange === '3months' ? 3 : dateRange === '6months' ? 6 : 12;
        const now = new Date();
        const currentYear = now.getFullYear();

        let oldestYear = currentYear;
        for (let i = monthsToShow - 1; i >= 0; i--) {
          const date = new Date(currentYear, now.getMonth() - i, 1);
          oldestYear = Math.min(oldestYear, date.getFullYear());

          const monthKey = date.toLocaleDateString('en-IN', {
            month: 'short',
            year: date.getFullYear() !== currentYear ? 'numeric' : undefined
          });

          const monthOrders = orders.filter(order => {
            const orderDate = new Date(order.created_at);
            return orderDate.getMonth() === date.getMonth() &&
              orderDate.getFullYear() === date.getFullYear();
          });

          let revenue;
          if (date.getMonth() === now.getMonth() && date.getFullYear() === currentYear) {
            revenue = Number(orderStats.monthly_revenue) || 0;
          } else {
            revenue = monthOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
          }

          data.push({ month: monthKey, revenue });
        }

        const yearRange = oldestYear === currentYear ? currentYear : `${oldestYear}–${currentYear}`;
        chartTitle = `Monthly Revenue Trend – ${yearRange}`;
        break;
      }
    }

    return { data, chartTitle };
  }, [orders, dateRange, orderStats.monthly_revenue]);

  // IMPROVED: Monthly Comparison Data
  const monthlyComparisonData = useMemo(() => {
    const data = [];
    let statsTitle = '';
    let inventoryCount = 0;
    let orderCount = 0;

    switch (dateRange) {
      case 'current_day': {
        const { start, end } = getStartAndEndOfDay();
        inventoryCount = items.filter(item => {
          const created = new Date(item.created_at);
          return created >= start && created <= end;
        }).length;
        orderCount = orders.filter(order => {
          const created = new Date(order.created_at);
          return created >= start && created <= end;
        }).length;
        statsTitle = "Today's Statistics";
        break;
      }
      case 'current_week': {
        const { start, end } = getStartAndEndOfWeek();
        inventoryCount = items.filter(item => {
          const created = new Date(item.created_at);
          return created >= start && created <= end;
        }).length;
        orderCount = orders.filter(order => {
          const created = new Date(order.created_at);
          return created >= start && created <= end;
        }).length;
        statsTitle = "This Week's Statistics";
        break;
      }
      case 'current_month': {
        const { start, end } = getStartAndEndOfMonth();
        inventoryCount = items.filter(item => {
          const created = new Date(item.created_at);
          return created >= start && created <= end;
        }).length;
        orderCount = orders.filter(order => {
          const created = new Date(order.created_at);
          return created >= start && created <= end;
        }).length;
        statsTitle = "This Month's Statistics";
        break;
      }
      default: { // For 3months, 6months, 12months
        const monthsToShow = dateRange === '3months' ? 3 : dateRange === '6months' ? 6 : 12;
        for (let i = monthsToShow - 1; i >= 0; i--) {
          const date = new Date(new Date().getFullYear(), new Date().getMonth() - i, 1);
          const monthKey = date.toLocaleString('en-US', { month: 'short' });
          const start = new Date(date.getFullYear(), date.getMonth(), 1);
          const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);

          data.push({
            month: monthKey,
            inventory: items.filter(item => {
              const created = new Date(item.created_at);
              return created >= start && created <= end;
            }).length,
            orders: orders.filter(order => {
              const created = new Date(order.created_at);
              return created >= start && created <= end;
            }).length,
          });
        }
        statsTitle = `Inventory (Last ${monthsToShow} Months)`;
        break;
      }
    }
    // For single period views, we create a single data point for the bar chart
    if (dateRange.includes('current')) {
      data.push({
        name: dateRange.replace('current_', '').charAt(0).toUpperCase() + dateRange.slice(9),
        inventory: inventoryCount,
        orders: orderCount,
      });
    }

    return { data, statsTitle };
  }, [items, orders, dateRange]);

  // IMPROVED: Recent Activity with more types
  const recentActivities = useMemo(() => {
    const activities = [];

    // New Orders
    orders.slice(0, 10).forEach(order => {
      activities.push({
        id: `order-${order.id}`,
        type: 'order',
        icon: ShoppingCart,
        color: 'text-purple-600',
        bg: 'bg-purple-50 dark:bg-purple-950/20',
        title: 'New Canteen Order',
        details: `${order.employee_name || 'Employee'} • ${order.items?.length || 0} items`,
        timestamp: order.created_at,
      });
    });

    // Low Stock Alerts (quantity < 10)
    items.filter(i => i.quantity < 10).slice(0, 5).forEach(item => {
      activities.push({
        id: `lowstock-${item.id}`,
        type: 'lowstock',
        icon: AlertCircle,
        color: 'text-orange-600',
        bg: 'bg-orange-50 dark:bg-orange-950/20',
        title: 'Low Stock Alert',
        details: `${item.sub_category_name || item.category_name || 'Item'} • Only ${item.quantity} left`,
        timestamp: item.updated_at || item.created_at,
      });
    });

    // New Inventory Added
    items.slice(0, 5).forEach(item => {
      activities.push({
        id: `inventory-${item.id}`,
        type: 'inventory',
        icon: Package2,
        color: 'text-blue-600',
        bg: 'bg-blue-50 dark:bg-blue-950/20',
        title: 'New Inventory Added',
        details: `${item.sub_category_name || 'Item'} • ${item.quantity} units`,
        timestamp: item.created_at,
      });
    });

    // NEW: New Employees
    employees.slice(0, 5).forEach(employee => {
      activities.push({
        id: `employee-${employee.id}`,
        type: 'employee',
        icon: UserPlus,
        color: 'text-green-600',
        bg: 'bg-green-50 dark:bg-green-950/20',
        title: 'New Employee Added',
        details: `${employee.first_name} ${employee.last_name} • ${employee.role_name || 'No role'}`,
        timestamp: employee.created_at,
      });
    });

    // Sort by timestamp (newest first)
    return activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5); // Show more activities
  }, [orders, items, employees]);


  const stats = [
    { title: 'Total Employees', value: employees.length, icon: Users, color: 'blue' },
    { title: 'Roles', value: roles.length, icon: Shield, color: 'indigo' },
    { title: 'Departments', value: departments.length, icon: Building2, color: 'purple' },
    { title: 'Categories', value: categories.length, icon: FolderOpen, color: 'teal' },
    { title: 'Subcategories', value: subcategories.length, icon: Folder, color: 'cyan' },
    { title: 'Inventory Items', value: items.length, icon: Package, color: 'green' },
    { title: 'Menu Items', value: menus.length, icon: UtensilsCrossed, color: 'orange' },
    { title: 'Total Orders', value: orders.length, icon: IndianRupee, color: 'amber' },
    { title: 'Open Tickets', value: tickets.filter(t => t.status !== 'closed').length, icon: Ticket, color: 'green' },
    { title: 'Closed Tickets', value: tickets.filter(t => t.status === 'closed').length, icon: Ticket, color: 'red' },
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-950/30',
    indigo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-950/30',
    teal: 'bg-teal-50 text-teal-600 dark:bg-teal-950/30',
    cyan: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950/30',
    green: 'bg-green-50 text-green-600 dark:bg-green-950/30',
    orange: 'bg-orange-50 text-orange-600 dark:bg-orange-950/30',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-950/30',
    red: 'bg-red-50 text-red-600 dark:bg-red-950/30',
  };

  const formatTimeAgo = (dateString) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return 'Earlier';
  };

  // NEW: Export function
  const exportData = () => {
    // Implementation for exporting dashboard data
    toast.info('Export feature coming soon!');
  };

  // NEW: Loading state component
  // if (isLoading) {
  //   return (
  //     <div className="flex items-center justify-center h-96">
  //       <div className="text-center">
  //         <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
  //         <p className="text-lg font-medium">Loading dashboard data...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // NEW: Error state component
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={refreshData} className="mx-auto">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, highlight = false }) => {
    const colors = {
      blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30",
      emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30",
      red: "bg-red-100 text-red-700 dark:bg-red-900/30",
      green: "bg-green-100 text-green-700 dark:bg-green-900/30",
      purple: "bg-purple-100 text-purple-700 dark:bg-purple-900/30",
      orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/30",
      indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30",
    };

    return (
      <div className={cn(
        "rounded-2xl p-5 border shadow-sm transition-all hover:shadow-md",
        highlight && "ring-2 ring-primary/20 bg-gradient-to-br from-primary/5"
      )}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className={cn("text-2xl font-bold mt-2", highlight && "text-primary")}>
              {value}
            </p>
          </div>
          <div className={cn("p-3 rounded-full", colors[color] || colors.blue)}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  const StatusProgress = ({ stats, total }) => (
    <div className="space-y-3 bg-muted/30 rounded-xl p-5">
      <p className="text-sm font-medium text-muted-foreground">Order Status Distribution</p>
      <div className="space-y-2">
        {[
          { label: "Pending", value: stats.pending, color: "bg-amber-500" },
          { label: "Preparing", value: stats.preparing, color: "bg-blue-500" },
          { label: "Ready", value: stats.ready, color: "bg-orange-500" },
          { label: "Delivered", value: stats.delivered, color: "bg-emerald-500" },
          { label: "Cancelled", value: stats.cancelled, color: "bg-red-500" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-20">{item.label}</span>
            <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
              <div
                className={cn("h-full transition-all duration-700", item.color)}
                style={{ width: `${(item.value / total) * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium w-12 text-right">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const TodayStatusBars = ({ stats }) => (
    <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/20 dark:to-blue-950/20 rounded-xl p-5">
      <p className="text-sm font-semibold mb-4">Today's Flow</p>
      <div className="grid grid-cols-5 gap-2 text-center">
        {[
          { label: "Pending", value: stats.today_total_pending, color: "bg-amber-400" },
          { label: "Prep", value: stats.today_total_preparing, color: "bg-blue-400" },
          { label: "Ready", value: stats.today_total_ready, color: "bg-orange-400" },
          { label: "Delivered", value: stats.today_total_delivered, color: "bg-emerald-400" },
          { label: "Cancel", value: stats.today_total_cancelled, color: "bg-red-400" },
        ].map((item) => (
          <div key={item.label} className="space-y-2">
            <div className={cn("rounded-lg p-3 text-white font-bold text-lg", item.color)}>
              {item.value}
            </div>
            <p className="text-xs text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const MonthlyProgress = ({ stats, total }) => (
    <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-6 border">
      <div className="flex justify-between items-center mb-4">
        <p className="font-semibold">This Month Performance</p>
        <Badge variant="secondary" className="font-bold">
          ₹{stats.monthly_revenue?.toLocaleString('en-IN')}
        </Badge>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span>Delivered Rate</span>
          <span className="font-medium text-emerald-600">
            {total > 0 ? Math.round((stats.monthly_total_delivered / total) * 100) : 0}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div
            className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-1000"
            style={{ width: `${total > 0 ? (stats.monthly_total_delivered / total) * 100 : 0}%` }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {canRead &&
        <div className="space-y-8 animate-fade-in">
          {/* NEW: Header with actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Dashboard Overview
              </h1>
              <p className="text-muted-foreground mt-2">Real-time insights across authorized modules</p>
            </div>

          </div>

          <Attendance />

          {showFullDashboard &&
            <>
              {/* Stats Grid */}
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
                {stats.map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <Card key={i} className="hover:shadow-lg transition-shadow duration-300 border-0 shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                            <p className="text-3xl font-bold">{stat.value}</p>
                            <Badge variant="secondary" className="mt-2 text-xs">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              Live
                            </Badge>
                          </div>
                          <div className={cn("p-3 rounded-xl", colorClasses[stat.color])}>
                            <Icon className="w-7 h-7" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Charts Section */}
              <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
                {/* Monthly Revenue Trend */}
                <Card className="xl:col-span-2">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                    <CardTitle>{monthlyRevenueData.chartTitle}</CardTitle>
                    <CardDescription>
                      {dateRange.includes('current') ? 'Detailed breakdown' : 'Canteen earnings'} • Today: ₹{(orderStats.today_revenue || 0).toLocaleString('en-IN')}
                    </CardDescription>
                    </div>
                    {showFullDashboard &&
                      <div className="flex items-center gap-2">
                        {/* NEW: Date range selector */}
                        <Select value={dateRange} onValueChange={setDateRange}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="current_day">Current Day</SelectItem>
                            <SelectItem value="current_week">Current Week</SelectItem>
                            <SelectItem value="current_month">Current Month</SelectItem>
                            <SelectItem value="3months">Last 3 Months</SelectItem>
                            <SelectItem value="6months">Last 6 Months</SelectItem>
                            <SelectItem value="12months">Last 12 Months</SelectItem>
                          </SelectContent>
                        </Select>

                      </div>}
                  </CardHeader>
                  <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '500px', justifyContent: 'flex-end' }}>
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={monthlyRevenueData.data}>
                          <CartesianGrid strokeDasharray="4 4" className="stroke-muted" />
                          <XAxis
                            dataKey={dateRange === 'current_day' || dateRange === 'current_week' ? 'day' : 'month'}
                            className="text-sm"
                            interval="preserveStartEnd"
                            tick={{ fontSize: 12 }}
                            height={60}
                            angle={dateRange === 'current_week' ? -45 : 0}
                            textAnchor={dateRange === 'current_week' ? 'end' : 'middle'}
                          />
                          <YAxis
                            className="text-sm"
                            tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(1)}k` : `₹${v}`}
                          />
                          <Tooltip
                            formatter={(value) => `₹${Number(value).toLocaleString('en-IN')}`}
                            labelFormatter={(label) => `${label} Revenue`}
                          />
                          <Line
                            type="monotone"
                            dataKey="revenue"
                            stroke="#10b981"
                            strokeWidth={4}
                            dot={{ fill: '#10b981', r: 6 }}
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Live Orders Status */}
                {canViewOrder && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Live Orders Status</CardTitle>
                      <CardDescription>Real-time order distribution</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Pending', value: orderStats.pending || 0 },
                              { name: 'Preparing', value: orderStats.preparing || 0 },
                              { name: 'Ready', value: orderStats.ready || 0 },
                              { name: 'Delivered', value: orderStats.delivered || 0 },
                            ]}
                            cx="50%" cy="50%" innerRadius={70} outerRadius={110}
                            paddingAngle={4} dataKey="value"
                            stroke="hsl(var(--background))" strokeWidth={3}
                          >
                            <Cell fill="#f59e0b" />
                            <Cell fill="#3b82f6" />
                            <Cell fill="#f97316" />
                            <Cell fill="#10b981" />
                          </Pie>
                          <Tooltip formatter={(v) => `${v} orders`} />
                        </PieChart>
                      </ResponsiveContainer>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-amber-500" /><span>Pending: <strong>{orderStats.pending || 0}</strong></span></div>
                        <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-blue-500" /><span>Preparing: <strong>{orderStats.preparing || 0}</strong></span></div>
                        <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-orange-500" /><span>Ready: <strong>{orderStats.ready || 0}</strong></span></div>
                        <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-emerald-500" /><span>Delivered: <strong>{orderStats.delivered || 0}</strong></span></div>
                      </div>

                      <div className="text-center pt-4 border-t">
                        <p className="text-sm text-muted-foreground">Today's Revenue</p>
                        <p className="text-3xl font-bold text-emerald-600 mt-1">₹{(orderStats.today_revenue || 0).toLocaleString('en-IN')}</p>
                        <p className="text-xs text-muted-foreground mt-1">{orderStats.today_total || 0} orders today</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Monthly Statistics – Inventory vs Orders */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle>{monthlyComparisonData.statsTitle}</CardTitle>
                  {showFullDashboard &&
                    <div className="flex items-center gap-2">
                      {/* NEW: Date range selector */}
                      <Select value={dateRange} onValueChange={setDateRange}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="current_day">Current Day</SelectItem>
                          <SelectItem value="current_week">Current Week</SelectItem>
                          <SelectItem value="current_month">Current Month</SelectItem>
                          <SelectItem value="3months">Last 3 Months</SelectItem>
                          <SelectItem value="6months">Last 6 Months</SelectItem>
                          <SelectItem value="12months">Last 12 Months</SelectItem>
                        </SelectContent>
                      </Select>

                    </div>}
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={monthlyComparisonData.data}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey={dateRange.includes('current') ? 'name' : 'month'}
                        tick={{ fontSize: 14 }}
                      />
                      <YAxis tick={{ fontSize: 14 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                        labelStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      <Legend />
                      <Bar dataKey="inventory" fill="#3b82f6" name="Inventory Added" radius={[8, 8, 0, 0]} />
                      {/* <Bar dataKey="orders" fill="#14b8a6" name="Orders Placed" radius={[8, 8, 0, 0]} /> */}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          }
        </div>}
    </>
  );
};

export default Dashboard;